import {
  ComponentType,
  MouseEvent,
  MouseEventHandler,
  PropsWithChildren,
  useContext,
} from 'react';
import { LinkProps, NavLinkProps, useNavigate } from 'react-router-dom';

import { TrackedNativeLink } from 'components/Link/TrackedNativeLink';
import { UpdateRequiredContext } from 'containers/UpdateRequiredContext';
import { deferAndStartRouteLoadingAnimation } from 'utils/ui/routeLoadingAnimation';
import { getUrlWithoutOrigin } from 'utils/url/getUrlWithoutOrigin';
import { isUrlWithProtocol } from 'utils/url/isUrlWithProtocol';

type VersionedBaseLinkProps =
  | {
      className?: string;
      component: ComponentType<LinkProps>;
      'data-qa-id'?: string;
      onClick?: MouseEventHandler<HTMLAnchorElement>;
      to: LinkProps['to'];
      state?: LinkProps['state'];
      tabIndex?: number;
      target?: LinkProps['target'];
    }
  | {
      className?: string;
      component: ComponentType<NavLinkProps>;
      'data-qa-id'?: string;
      onClick?: MouseEventHandler<HTMLAnchorElement>;
      to: NavLinkProps['to'];
      state?: NavLinkProps['state'];
      tabIndex?: number;
      target?: LinkProps['target'];
    };

export function VersionedBaseLink({
  children,
  className,
  component,
  to,
  onClick,
  state,
  tabIndex,
  target,
  ...props
}: PropsWithChildren<VersionedBaseLinkProps>) {
  const Component = component;

  const updateRequired = useContext(UpdateRequiredContext);
  const navigate = useNavigate();

  const deferredOnClick =
    target && target !== '_self'
      ? onClick
      : (event: MouseEvent<HTMLAnchorElement>) => {
          event.preventDefault();
          deferAndStartRouteLoadingAnimation(() => {
            onClick?.(event);

            const normalizedTo =
              typeof to === 'string' && isUrlWithProtocol(to)
                ? getUrlWithoutOrigin(to)
                : to;

            navigate(normalizedTo, { state });
          });
        };

  if (updateRequired && typeof to === 'string' && !state) {
    return (
      <TrackedNativeLink
        className={className}
        data-qa-id={props['data-qa-id']}
        onClick={deferredOnClick}
        href={to}
        eventName="Updating App Version"
        tabIndex={tabIndex}
        target={target}
      >
        {children}
      </TrackedNativeLink>
    );
  }

  return (
    <Component
      className={className}
      data-qa-id={props['data-qa-id']}
      onClick={deferredOnClick}
      to={to}
      state={state}
      tabIndex={tabIndex}
      target={target}
    >
      {children}
    </Component>
  );
}
